import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useCanonicalOpportunities, useCreateCanonicalOpportunity, useNoticeCanonicalLinks, useNoticeLinkMutations } from '../hooks/useCanonicalOpportunities';
import { QualificationSection } from './QualificationSection';
/** Notice detail section: canonical Opportunities this Notice is linked to (SAD-469). */ export function LinkedOpportunities({ noticeId }) {
    const location = useLocation();
    const contextLink = (id)=>{
        const params = new URLSearchParams(location.search);
        params.set('contextOpportunity', id);
        params.delete('contextAssessment');
        return `/opportunities?${params}`;
    };
    const [pickedId, setPickedId] = useState('');
    const [newTitle, setNewTitle] = useState('');
    const { linked, isLoading, error } = useNoticeCanonicalLinks(noticeId);
    const { data: all = [] } = useCanonicalOpportunities();
    const { link, unlink } = useNoticeLinkMutations(noticeId);
    const create = useCreateCanonicalOpportunity();
    const linkedIds = new Set(linked.map((l)=>l.opportunityId));
    const candidates = all.filter((o)=>!linkedIds.has(o.id));
    const fail = (what)=>()=>toast.error(`Could not ${what}`);
    const linkExisting = ()=>{
        if (!pickedId) return;
        link.mutate(pickedId, {
            onSuccess: ()=>setPickedId(''),
            onError: fail('link opportunity')
        });
    };
    const linkNew = ()=>{
        const title = newTitle.trim();
        if (!title) return;
        create.mutate(title, {
            onSuccess: (opp)=>{
                if (!opp) return;
                link.mutate(opp.id, {
                    onSuccess: ()=>setNewTitle(''),
                    onError: fail('link opportunity')
                });
            },
            onError: fail('create opportunity')
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.canonicalOpportunity.linkedSection,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 text-sm text-muted-foreground mb-2",
                children: [
                    /*#__PURE__*/ _jsx(Link2, {
                        className: "h-4 w-4"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: "Linked Opportunities"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "ml-6 space-y-3",
                children: [
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Loading…"
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Could not load linked opportunities."
                    }),
                    !isLoading && !error && linked.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        "data-testid": Testids.canonicalOpportunity.linkedEmpty,
                        children: "Not linked to any opportunity."
                    }),
                    linked.map((l)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-2 text-sm",
                            "data-testid": Testids.canonicalOpportunity.linkedItem,
                            "data-opportunity-id": l.opportunityId,
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        l.title,
                                        " ·",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: contextLink(l.opportunityId),
                                            children: "Customer & contracting office"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "sm",
                                    variant: "ghost",
                                    disabled: unlink.isPending,
                                    "aria-label": `Unlink ${l.title}`,
                                    onClick: ()=>unlink.mutate(l.opportunityId, {
                                            onError: fail('unlink opportunity')
                                        }),
                                    "data-testid": Testids.canonicalOpportunity.unlinkButton,
                                    children: /*#__PURE__*/ _jsx(Unlink, {
                                        className: "h-4 w-4"
                                    })
                                })
                            ]
                        }, l.linkId)),
                    linked.map((l)=>/*#__PURE__*/ _jsx(QualificationSection, {
                            opportunityId: l.opportunityId
                        }, l.linkId)),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                size: "sm",
                                className: "flex-1",
                                value: pickedId,
                                onChange: (e)=>setPickedId(e.target.value),
                                "aria-label": "Existing opportunity",
                                "data-testid": Testids.canonicalOpportunity.pickerSelect,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Link to existing opportunity…"
                                    }),
                                    candidates.map((o)=>/*#__PURE__*/ _jsx("option", {
                                            value: o.id,
                                            children: o.title
                                        }, o.id))
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                disabled: !pickedId || link.isPending,
                                onClick: linkExisting,
                                "data-testid": Testids.canonicalOpportunity.linkExistingButton,
                                children: "Link"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Input, {
                                value: newTitle,
                                onChange: (e)=>setNewTitle(e.target.value),
                                placeholder: "Or create a new opportunity",
                                "aria-label": "New opportunity title",
                                "data-testid": Testids.canonicalOpportunity.linkNewTitleInput
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                disabled: !newTitle.trim() || create.isPending || link.isPending,
                                onClick: linkNew,
                                "data-testid": Testids.canonicalOpportunity.linkNewButton,
                                children: "Create & link"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
